import { Activity, Gauge, HeartPulse, Waves } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { useReplicaMetrics } from "@/api/local/runtime";
import { normalizeWorkloadSpec } from "@/api/local/workload";
import type { Pod, Pool, Replica } from "@/api/types";
import { MetricTile, type Tone } from "@/components/MetricTile";
import { Pill, type PillTone } from "@/components/Pill";
import { Section } from "@/components/Section";
import { Sparkline } from "@/components/Sparkline";

interface Props {
  pod: Pod | null;
  replica: Replica;
  pool: Pool;
}

/**
 * Pod-level health and Prometheus snapshot.
 *
 * Backed by `/api/v1/metrics/replicas/{replica_id}`, which proxies a
 * small set of workload-neutral PromQL queries (probe `up`, Envoy
 * throughput, the system-wide `queue_depth_ms` convention). GPU /
 * vLLM-specific tiles (`tok/s`, `GPU util`, `VRAM used`) are
 * deliberately not surfaced here — backstage runs arbitrary HTTP
 * workloads and a CPU echo pool reading 0 on those tiles is worse
 * than not showing them. When Prometheus has nothing yet the
 * controller returns zeros and the remaining tiles render a "no data"
 * state without falling over.
 */
export function PodMetrics({ pod: _pod, replica, pool }: Props) {
  const m = useReplicaMetrics(replica.id);

  // Pool-level workload wins when the pool has detached from the
  // service-scope spec; otherwise the controller's renderer falls
  // back to the service workload (which we don't have on this page —
  // the default-coalescing inside `normalizeWorkloadSpec` keeps the
  // display sensible either way).
  const workload = normalizeWorkloadSpec(pool.workload ?? null);
  const metricsPath = workload.metrics_path;
  const metricsPort = workload.port;

  // Live WS sample for the inline sparkline. We accumulate values
  // client-side as the pool-scoped runtime endpoint refreshes — the
  // controller exposes only the *current* count, not history (history
  // is in Prometheus and would be a separate query). 60 most-recent
  // samples is enough for a hint card.
  const runtime = useQuery<Record<number, ReplicaRuntime>>({
    queryKey: ["pool", pool.id, "replica-runtime"],
    queryFn: () =>
      api.get<Record<number, ReplicaRuntime>>(
        `/pools/${pool.id}/replica-runtime`,
      ),
    staleTime: 4_000,
    refetchInterval: 5_000,
  });
  const wsCurrent = runtime.data?.[replica.id]?.active_websockets ?? 0;
  const wsHistory = useWsHistory(replica.id, wsCurrent);

  const healthTone: Tone =
    m.health === "ok" ? "ok" : m.health === "degraded" ? "warn" : "danger";
  const healthPill: PillTone =
    m.health === "ok" ? "ok" : m.health === "degraded" ? "warn" : "danger";

  // Prefer the controller's `last_health_at` (DB-backed probe result);
  // fall back to the metrics endpoint's timestamp when the DB hasn't
  // observed a tick yet.
  const lastHealth = replica.last_health_at ?? m.last_health_at;
  const lastHealthAge = lastHealth
    ? formatRelative(Date.now() - new Date(lastHealth).getTime())
    : "never";

  return (
    <>
      <div className="metric-grid">
        <MetricTile
          icon={<HeartPulse />}
          label="Health"
          value={
            <Pill tone={healthPill}>
              {m.health}
            </Pill>
          }
          tone={healthTone}
          hint={
            lastHealth
              ? `last check ${lastHealthAge} ago`
              : "no successful health check yet"
          }
        />
        <MetricTile
          icon={<Activity />}
          label="Throughput"
          value={`${m.rps.toFixed(1)}`}
          unit="rps"
          hint={
            <Sparkline
              values={m.rps_history}
              width={140}
              height={28}
              tone="brand"
              fill
            />
          }
        />
        <MetricTile
          icon={<Gauge />}
          label="Queue depth"
          value={`${m.queue_depth_ms.toFixed(0)}`}
          unit="ms"
          tone={
            m.queue_depth_ms > 2000
              ? "danger"
              : m.queue_depth_ms > 800
                ? "warn"
                : "ok"
          }
          hint="rolling avg of inflight queue time (workload-emitted queue_depth_ms)"
        />
        <MetricTile
          icon={<Waves />}
          label="Active WS"
          value={`${wsCurrent}`}
          tone={wsCurrent > 0 ? "warn" : "ok"}
          hint={
            <Sparkline
              values={wsHistory}
              width={140}
              height={28}
              tone={wsCurrent > 0 ? "warn" : "muted"}
              fill
            />
          }
        />
      </div>

      <Section title="Health check history" icon={<HeartPulse />}>
        <div className="kv">
          <span className="k">controller status</span>
          <span className="v mono">{replica.status}</span>
          <span className="k">last_health_at</span>
          <span className="v mono">
            {lastHealth ? new Date(lastHealth).toLocaleString() : "—"}
          </span>
          <span className="k">last_update_age</span>
          <span className="v mono">{lastHealthAge}</span>
          <span className="k">uptime</span>
          <span className="v mono">{formatUptime(m.uptime_seconds)}</span>
          <span className="k">prometheus source</span>
          <span className="v mono">
            <code>{metricsPath}</code> on <code>:{metricsPort}</code>{" "}
            (scraped by GMP)
          </span>
        </div>
        <p className="muted" style={{ fontSize: 11.5, marginTop: 12 }}>
          Numbers above tick from a synthetic source pending the
          controller's <code>/api/v1/replicas/:id/metrics</code> endpoint.
          Health/last_update come from the live <code>replicas</code> row.
        </p>
      </Section>
    </>
  );
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hrs = Math.floor((seconds % 86_400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hrs}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
}

function formatRelative(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

interface ReplicaRuntime {
  active_websockets: number;
  in_flight_short_lived: number;
}

const WS_HISTORY_BUFFER: Record<number, number[]> = {};
const WS_HISTORY_LEN = 60;

/**
 * Accumulate WS sample history client-side. The pool-scoped runtime
 * endpoint only returns the current value; we ring-buffer in module
 * scope so navigating away/back doesn't lose the trace immediately.
 */
function useWsHistory(replicaId: number, value: number): number[] {
  const buf = WS_HISTORY_BUFFER[replicaId] ?? [];
  if (buf.length === 0 || buf[buf.length - 1] !== value) {
    buf.push(value);
    if (buf.length > WS_HISTORY_LEN) buf.shift();
    WS_HISTORY_BUFFER[replicaId] = buf;
  }
  return buf.slice();
}
